import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  DESCRIPTIONS_ROLE,
  LIBELLES_APPARENCE,
  LIBELLES_ROLE,
  ROLES,
  dateHeureTunis,
  type Apparence,
  type Role,
  type UtilisateurPublic,
} from '@rh/shared';
import { appel } from '../api';
import { useAuth } from '../auth/contexte-auth';
import { ChoixApparence, IconeApparence } from '../composants/ChoixApparence';
import { IconeTelephone } from '../composants/DernierAppel';
import { Chargement, Erreur } from '../composants/Etat';
import { NavAdministration } from '../composants/NavAdministration';
import { motDePasseProvisoire } from '../mot-de-passe-provisoire';
import { appliquerApparence } from '../theme';

interface Annonce {
  texte: string;
  motDePasse?: string;
}

const trier = (comptes: UtilisateurPublic[]) =>
  [...comptes].sort((a, b) => Number(b.actif) - Number(a.actif) || a.nom.localeCompare(b.nom, 'fr'));

/** Comptes et rôles (docs/UI.md § Écran 6). */
export function AdminComptes() {
  const { utilisateur } = useAuth();
  const [comptes, setComptes] = useState<UtilisateurPublic[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [annonce, setAnnonce] = useState<Annonce | null>(null);

  useEffect(() => {
    appel<UtilisateurPublic[]>('/utilisateurs')
      .then((liste) => setComptes(trier(liste)))
      .catch((e: Error) => setErreur(e.message));
  }, []);

  function remplacer(compte: UtilisateurPublic): void {
    setComptes((liste) => trier([...(liste ?? []).filter((c) => c.id !== compte.id), compte]));
  }

  return (
    <section>
      <NavAdministration />
      <div className="entete-annonce">
        <h2>Comptes et rôles</h2>
      </div>
      <ul className="mention">
        {ROLES.map((role) => (
          <li key={role}>
            <strong>{LIBELLES_ROLE[role]}</strong> : {DESCRIPTIONS_ROLE[role]}
          </li>
        ))}
      </ul>

      {annonce && (
        <div className="notice-cv">
          <p role="status">{annonce.texte}</p>
          {/* Hors de la zone lue à voix haute : le mot de passe ne s'annonce pas. */}
          {annonce.motDePasse && (
            <>
              <p>
                Mot de passe provisoire à transmettre : <code className="mot-de-passe-affiche">{annonce.motDePasse}</code>
              </p>
              <p>
                Il n’est enregistré nulle part et disparaît dès que vous le masquez ou quittez la page. La personne le
                remplace dans « Mon compte » (clic sur son nom, en haut).
              </p>
              <button type="button" className="bouton-second" onClick={() => setAnnonce({ texte: annonce.texte })}>
                Mot de passe transmis : le masquer
              </button>
            </>
          )}
        </div>
      )}

      <NouveauCompte
        onCree={(compte, motDePasse) => {
          remplacer(compte);
          setAnnonce({ texte: `Compte créé pour ${compte.nom} (${compte.email}).`, motDePasse });
        }}
      />

      <section className="bloc">
        <h3>Comptes ({comptes?.length ?? '…'})</h3>
        {erreur && <Erreur message={erreur} />}
        {!comptes && !erreur && <Chargement quoi="des comptes" />}
        <ul className="liste-admin">
          {(comptes ?? []).map((compte) => (
            <LigneCompte
              key={compte.id}
              compte={compte}
              moi={compte.id === utilisateur?.id}
              onModifie={(maj, texte, motDePasse) => {
                remplacer(maj);
                setAnnonce({ texte, motDePasse });
              }}
            />
          ))}
        </ul>
      </section>
    </section>
  );
}

/** Droit « Voir les téléphones » : à cocher pour un lecteur, acquis d'office aux profils RH. */
function ChoixTelephones({
  role,
  valeur,
  onChange,
}: {
  role: Role;
  valeur: boolean;
  onChange: (valeur: boolean) => void;
}) {
  const lecteur = role === 'lecteur';
  return (
    <>
      <label className="case">
        <input
          type="checkbox"
          checked={lecteur ? valeur : true}
          disabled={!lecteur}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>Voir les téléphones des candidats</span>
      </label>
      <span className="mention">
        {lecteur
          ? 'Numéro, bouton Appeler et dernier appel sur la fiche candidat. E-mail, adresse et CV restent réservés aux profils RH.'
          : 'Toujours accordé aux rôles RH et super admin.'}
      </span>
    </>
  );
}

function NouveauCompte({ onCree }: { onCree: (compte: UtilisateurPublic, motDePasse: string) => void }) {
  const id = useId();
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('rh');
  const [apparence, setApparence] = useState<Apparence>('clair');
  const [voirTelephones, setVoirTelephones] = useState(false);
  const [deplacerCandidature, setDeplacerCandidature] = useState(false);
  const [motDePasse, setMotDePasse] = useState(motDePasseProvisoire);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  async function creer(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    if (!nom.trim() || !email.trim()) {
      setErreur('Nom et e-mail sont obligatoires');
      return;
    }
    setEnvoi(true);
    setErreur(null);
    try {
      const compte = await appel<UtilisateurPublic>('/utilisateurs', {
        method: 'POST',
        body: JSON.stringify({
          nom: nom.trim(),
          email: email.trim(),
          role,
          apparence,
          voirTelephones: role === 'lecteur' && voirTelephones,
          deplacerCandidature: role === 'rh' && deplacerCandidature,
          motDePasse,
        }),
      });
      onCree(compte, motDePasse);
      setNom('');
      setEmail('');
      setRole('rh');
      setApparence('clair');
      setVoirTelephones(false);
      setDeplacerCandidature(false);
      setMotDePasse(motDePasseProvisoire());
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Création impossible');
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <section className="bloc">
      <h3>Nouveau compte</h3>
      <form className="formulaire-admin" noValidate onSubmit={(e) => void creer(e)}>
        <div className="champ">
          <label htmlFor={`${id}-nom`}>Nom affiché</label>
          <input id={`${id}-nom`} value={nom} maxLength={200} autoComplete="off" onChange={(e) => setNom(e.target.value)} />
        </div>
        <div className="champ">
          <label htmlFor={`${id}-email`}>E-mail de connexion</label>
          <input
            id={`${id}-email`}
            type="email"
            value={email}
            maxLength={320}
            autoComplete="off"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="champ">
          <label htmlFor={`${id}-role`}>Rôle</label>
          <select id={`${id}-role`} value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {LIBELLES_ROLE[r]}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <span>Mot de passe provisoire</span>
          <span>
            <code className="mot-de-passe-affiche">{motDePasse}</code>{' '}
            <button type="button" className="lien" onClick={() => setMotDePasse(motDePasseProvisoire())}>
              En tirer un autre
            </button>
          </span>
        </div>
        <div className="champ pleine">
          <ChoixTelephones role={role} valeur={voirTelephones} onChange={setVoirTelephones} />
        </div>
        <div className="champ pleine">
          <ChoixDeplacement role={role} valeur={deplacerCandidature} onChange={setDeplacerCandidature} />
        </div>
        <div className="champ pleine">
          <ChoixApparence nom={`${id}-apparence`} valeur={apparence} onChange={setApparence} />
        </div>
        <div className="boutons">
          <button type="submit" className="bouton" disabled={envoi}>
            Créer le compte
          </button>
        </div>
      </form>
      {erreur && <Erreur message={erreur} />}
    </section>
  );
}

/**
 * Droit de déplacer une candidature d'une annonce à une autre (migration 024) :
 * le super admin l'a toujours, un profil RH seulement si on le coche ici. Un
 * lecteur n'écrit rien, le droit ne lui sert pas.
 */
function ChoixDeplacement({
  role,
  valeur,
  onChange,
}: {
  role: Role;
  valeur: boolean;
  onChange: (valeur: boolean) => void;
}) {
  const concerne = role === 'rh';
  return (
    <>
      <label className="case">
        <input
          type="checkbox"
          checked={concerne ? valeur : role === 'super_admin'}
          disabled={!concerne}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>Déplacer une candidature vers une autre annonce</span>
      </label>
      <span className="mention">
        {concerne
          ? 'Bouton « Déplacer vers une autre annonce » sur la fiche candidat : la candidature change d’annonce, avec ses appels, son évaluation et ses documents.'
          : role === 'super_admin'
            ? 'Toujours accordé au super admin.'
            : 'Sans objet pour un lecteur, qui ne modifie aucun dossier.'}
      </span>
    </>
  );
}

function LigneCompte({
  compte,
  moi,
  onModifie,
}: {
  compte: UtilisateurPublic;
  moi: boolean;
  onModifie: (compte: UtilisateurPublic, texte: string, motDePasse?: string) => void;
}) {
  const id = useId();
  const [edition, setEdition] = useState(false);
  const [nom, setNom] = useState(compte.nom);
  const [role, setRole] = useState<Role>(compte.role);
  const [apparence, setApparence] = useState<Apparence>(compte.apparence);
  const [voirTelephones, setVoirTelephones] = useState(compte.voirTelephones);
  const [deplacerCandidature, setDeplacerCandidature] = useState(compte.deplacerCandidature);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  async function envoyer(corps: Record<string, unknown>, texte: string, motDePasse?: string): Promise<boolean> {
    setEnvoi(true);
    setErreur(null);
    try {
      const maj = await appel<UtilisateurPublic>(`/utilisateurs/${compte.id}`, {
        method: 'PUT',
        body: JSON.stringify(corps),
      });
      onModifie(maj, texte, motDePasse);
      return true;
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Modification impossible');
      return false;
    } finally {
      setEnvoi(false);
    }
  }

  async function enregistrer(evenement: FormEvent): Promise<void> {
    evenement.preventDefault();
    const corps: Record<string, unknown> = {};
    if (nom.trim() && nom.trim() !== compte.nom) corps.nom = nom.trim();
    if (!moi && role !== compte.role) corps.role = role;
    if (apparence !== compte.apparence) corps.apparence = apparence;
    // Le droit ne sert qu'au lecteur : un compte promu RH le perd, pour ne pas le retrouver s'il redevient lecteur.
    const droit = role === 'lecteur' && voirTelephones;
    if (droit !== compte.voirTelephones) corps.voirTelephones = droit;
    // Même règle pour le déplacement : il ne se coche que sur un compte RH.
    const deplacement = role === 'rh' && deplacerCandidature;
    if (deplacement !== compte.deplacerCandidature) corps.deplacerCandidature = deplacement;
    if (Object.keys(corps).length === 0) {
      setEdition(false);
      return;
    }
    if (await envoyer(corps, `Compte de ${nom.trim() || compte.nom} modifié.`)) {
      // Son propre style change sous ses yeux, sans attendre le prochain renouvellement de session.
      if (moi && corps.apparence) appliquerApparence(apparence);
      setEdition(false);
    }
  }

  async function basculerActif(): Promise<void> {
    const question = compte.actif
      ? `Désactiver le compte de ${compte.nom} ? Ses sessions sont fermées aussitôt et il ne peut plus se connecter.`
      : `Réactiver le compte de ${compte.nom} ?`;
    if (!window.confirm(question)) return;
    await envoyer(
      { actif: !compte.actif },
      compte.actif ? `Compte de ${compte.nom} désactivé.` : `Compte de ${compte.nom} réactivé.`,
    );
  }

  async function nouveauMotDePasse(): Promise<void> {
    if (
      !window.confirm(
        `Donner un nouveau mot de passe provisoire à ${compte.nom} ? L’ancien ne marche plus et ses sessions sont fermées.`,
      )
    ) {
      return;
    }
    const motDePasse = motDePasseProvisoire();
    await envoyer({ motDePasse }, `Nouveau mot de passe pour ${compte.nom}.`, motDePasse);
  }

  return (
    <li className={compte.actif ? undefined : 'inactif'}>
      <div className="ligne-admin">
        <strong>{compte.nom}</strong>
        {moi && <span className="etiquette">vous</span>}
        <span className={`etiquette role-${compte.role}`}>{LIBELLES_ROLE[compte.role]}</span>
        <span className="etiquette" title="Style d’affichage">
          <IconeApparence apparence={compte.apparence} />
          {LIBELLES_APPARENCE[compte.apparence]}
        </span>
        {compte.role === 'lecteur' && compte.voirTelephones && (
          <span className="etiquette" title="Voit le téléphone des candidats sur leur fiche">
            <IconeTelephone /> Téléphones
          </span>
        )}
        {!compte.actif && <span className="etiquette alerte-etiquette">Désactivé</span>}
        <div className="actions">
          <button type="button" className="bouton-second" disabled={envoi} onClick={() => setEdition((v) => !v)}>
            {edition ? 'Fermer' : 'Modifier'}
          </button>
          {moi ? (
            <Link to="/mon-compte" className="bouton-second">
              Changer mon mot de passe
            </Link>
          ) : (
            <button type="button" className="bouton-second" disabled={envoi} onClick={() => void nouveauMotDePasse()}>
              Nouveau mot de passe
            </button>
          )}
          {!moi && (
            <button
              type="button"
              className={compte.actif ? 'bouton-second bouton-danger' : 'bouton-second'}
              disabled={envoi}
              onClick={() => void basculerActif()}
            >
              {compte.actif ? 'Désactiver' : 'Réactiver'}
            </button>
          )}
        </div>
      </div>
      <p className="mention">
        {compte.email} ·{' '}
        {compte.derniereConnexion ? `dernière connexion le ${dateHeureTunis(compte.derniereConnexion)}` : 'jamais connecté'}
      </p>
      {edition && (
        <form className="formulaire-admin" noValidate onSubmit={(e) => void enregistrer(e)}>
          <div className="champ">
            <label htmlFor={`${id}-nom`}>Nom affiché</label>
            <input id={`${id}-nom`} value={nom} maxLength={200} onChange={(e) => setNom(e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor={`${id}-role`}>Rôle</label>
            <select id={`${id}-role`} value={role} disabled={moi} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {LIBELLES_ROLE[r]}
                </option>
              ))}
            </select>
            {moi && <span className="mention">Votre propre rôle se change depuis un autre compte super admin.</span>}
          </div>
          <div className="champ pleine">
            <ChoixTelephones role={role} valeur={voirTelephones} onChange={setVoirTelephones} />
          </div>
          <div className="champ pleine">
            <ChoixDeplacement role={role} valeur={deplacerCandidature} onChange={setDeplacerCandidature} />
          </div>
          <div className="champ pleine">
            <ChoixApparence nom={`${id}-apparence`} valeur={apparence} onChange={setApparence} />
          </div>
          <div className="boutons">
            <button type="submit" className="bouton" disabled={envoi}>
              Enregistrer
            </button>
          </div>
        </form>
      )}
      {erreur && <Erreur message={erreur} />}
    </li>
  );
}
